import React from 'react';
import { buildMarketingMetaAdsFilterGroups } from './marketingMetaAdsFiltersConfig';

const currency = (n) => {
  if (n == null) return '\u2014';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
};
const number = (n) => (n == null ? '\u2014' : Number(n).toLocaleString());
const percent = (n) => (n == null ? '\u2014' : `${Number(n).toFixed(1)}%`);
const ratio = (n) => (n == null ? '\u2014' : `${Number(n).toFixed(2)}x`);

const FLAG_BADGES = {
  top_performer: { label: 'Top Performer', background: '#ecfdf3', color: '#027a48', border: '1px solid #6ce9a6' },
  underperforming: { label: 'Underperforming', background: '#fef3f2', color: '#b42318', border: '1px solid #fecdca' },
  at_risk: { label: 'At Risk', background: '#fffaeb', color: '#b54708', border: '1px solid #fedf89' },
};

export const META_ADS_COLUMNS = [
  { id: 'event_date', label: 'Event Date', sortable: true, sortType: 'date', align: 'left', first: true, alwaysVisible: true },
  { id: 'event_name', label: 'Event', sortable: true, sortType: 'text', align: 'left', alwaysVisible: true },
  { id: 'venue_name', label: 'Venue', sortable: true, sortType: 'text', align: 'left' },
  { id: 'performance_flag', label: 'Status', sortable: false, align: 'left' },
  { id: 'spend', label: 'Spend', sortable: true, sortType: 'number', align: 'right' },
  { id: 'opendate_revenue', label: 'Revenue (Opendate)', sortable: true, sortType: 'number', align: 'right' },
  { id: 'meta_revenue', label: 'Revenue (Meta)', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
  { id: 'opendate_roas', label: 'ROAS (Opendate)', sortable: true, sortType: 'number', align: 'right' },
  { id: 'meta_roas', label: 'ROAS (Meta)', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
  { id: 'opendate_tickets_sold', label: 'Tickets Sold', sortable: true, sortType: 'number', align: 'right' },
  { id: 'meta_purchases', label: 'Meta Purchases', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
  { id: 'meta_registrations', label: 'Meta Registrations', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
  { id: 'opendate_cpa', label: 'CPA (Opendate)', sortable: true, sortType: 'number', align: 'right' },
  { id: 'meta_cpa', label: 'CPA (Meta)', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
  { id: 'impressions', label: 'Impressions', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
  { id: 'clicks', label: 'Clicks', sortable: true, sortType: 'number', align: 'right' },
  { id: 'ctr', label: 'CTR', sortable: true, sortType: 'number', align: 'right' },
  { id: 'conversion_rate', label: 'Conv. Rate', sortable: true, sortType: 'number', align: 'right' },
  { id: 'days_until', label: 'Days Until', sortable: true, sortType: 'number', align: 'right' },
  { id: 'expected_finish', label: 'Expected Finish', sortable: true, sortType: 'number', align: 'right', defaultHidden: true },
];

export const META_ADS_SORT_TYPES = {
  text: { asc: 'A \u2192 Z', desc: 'Z \u2192 A' },
  number: { asc: 'Low \u2192 High', desc: 'High \u2192 Low' },
  date: { asc: 'Oldest first', desc: 'Newest first' },
};

export const META_ADS_CONFIG = {
  sidebarKey: 'marketing_meta_ads',
  pageTitle: 'Meta Ads',
  pageSubtitle: 'Spend, conversions, and pacing across your matched events.',
  defaultPreset: 'this_month',
  filterTitle: 'Filter Meta Ads',
  filterSubtitle: 'Apply filters to narrow Meta Ads data.',
  apiEndpoint: (teamId) => `/teams/${teamId}/reporting/marketing/meta-ads/data.json`,

  buildFilterGroups: buildMarketingMetaAdsFilterGroups,

  extraApiParams: (activeFilters) => {
    const params = {};
    if (activeFilters.event_name && activeFilters.event_name.length > 0) {
      params.event_name = activeFilters.event_name;
    }
    if (activeFilters.event_status && activeFilters.event_status.length > 0) {
      params.event_status = activeFilters.event_status;
    }
    return params;
  },

  columns: META_ADS_COLUMNS,
  sortTypes: META_ADS_SORT_TYPES,
  defaultSort: { field: 'spend', direction: 'desc' },
  localStorageKey: (teamId) => `reportingMarketingMetaAdsColumns_${teamId}`,
  nameColumn: 'event_name',

  getRows: (data) => (data && data.events) || [],
  getTotals: () => ({}),

  kpiColumns: 3,
  buildKpiCards: (kpis, prior) => [
    {
      top: {
        label: 'Total Spend', value: currency(kpis.total_spend || 0),
        current: kpis.total_spend, prior: prior.total_spend,
        tooltip: 'Total Meta Ads spend across matched events',
      },
    },
    {
      top: {
        label: 'Revenue (Meta)', value: currency(kpis.total_meta_revenue || 0),
        current: kpis.total_meta_revenue, prior: prior.total_meta_revenue,
        tooltip: 'Revenue Meta\'s pixel attributed to your ads',
      },
    },
    {
      top: {
        label: 'ROAS (Meta)', value: kpis.blended_roas_meta != null ? ratio(kpis.blended_roas_meta) : '\u2014',
        current: kpis.blended_roas_meta, prior: prior.blended_roas_meta,
        tooltip: 'Meta-attributed revenue \u00f7 spend',
      },
    },
  ],

  engagementTitle: 'Spend Efficiency Over Time',
  engagementSubtitle: 'Daily spend versus conversions',
  engagementSeries: [
    { dataKey: 'spend', label: 'Spend ($)', color: '#1982C4' },
    { dataKey: 'meta_purchases', label: 'Meta Purchases', color: '#6A4C93' },
    { dataKey: 'meta_registrations', label: 'Meta Registrations', color: '#067647' },
  ],
  engagementYFormatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toLocaleString()),

  buildFunnelData: (kpis) => [
    { name: 'Impressions', value: kpis.total_impressions || 0 },
    { name: 'Clicks', value: kpis.total_clicks || 0 },
    { name: 'Conversions', value: kpis.total_opendate_orders || 0 },
  ],
  funnelHasData: (kpis) => (kpis.total_impressions || 0) > 0,
  funnelLabel: 'count',
  funnelYAxisWidth: 100,
  secondChartTitle: 'Funnel',
  secondChartSubtitle: 'Impressions \u2192 clicks \u2192 conversions',

  topPerformersTitle: 'Top Performing Events',
  topPerformersSubtitle: 'Ranked by ROAS (Meta)',
  buildTopPerformers: (rows) =>
    [...rows]
      .filter((r) => r.meta_roas != null && r.meta_roas > 0)
      .sort((a, b) => (b.meta_roas || 0) - (a.meta_roas || 0))
      .slice(0, 10)
      .map((r) => ({ ...r, name: r.event_name })),
  topPerformersSeries: [
    { dataKey: 'meta_roas', label: 'ROAS', color: '#1982C4' },
  ],
  topPerformersYFormatter: (v) => `${v.toFixed(2)}x`,

  tableTitle: 'Events',
  searchPlaceholder: 'Search events...',
  emptyMessage: 'No event spend data for the selected period.',
  emptySearchMessage: 'No events match your search.',
  paginationLabel: 'events',
  csvFilename: 'meta_ads_report.csv',

  searchFilter: (r, q) =>
    (r.event_name || '').toLowerCase().includes(q) ||
    (r.venue_name || '').toLowerCase().includes(q) ||
    (r.presenter || '').toLowerCase().includes(q),

  renderCellValue: (colId, row, formatDate) => {
    switch (colId) {
      case 'event_date': return row.event_date ? formatDate(row.event_date) : '\u2014';
      case 'event_name': return (
        <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.event_name || '\u2014'}
        </div>
      );
      case 'venue_name': return (
        <div style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.venue_name || '\u2014'}
        </div>
      );
      case 'performance_flag': {
        if (!row.performance_flag) return '\u2014';
        const badge = FLAG_BADGES[row.performance_flag];
        if (!badge) return '\u2014';
        return (
          <span className="badge" style={{
            background: badge.background, color: badge.color, border: badge.border,
            fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 12,
          }}>{badge.label}</span>
        );
      }
      case 'spend': return currency(row.spend);
      case 'opendate_revenue': return currency(row.opendate_revenue);
      case 'meta_revenue': return currency(row.meta_revenue);
      case 'opendate_roas': return ratio(row.opendate_roas);
      case 'meta_roas': return ratio(row.meta_roas);
      case 'opendate_tickets_sold': return number(row.opendate_tickets_sold);
      case 'meta_purchases': return number(row.meta_purchases);
      case 'meta_registrations': return number(row.meta_registrations);
      case 'opendate_cpa': return currency(row.opendate_cpa);
      case 'meta_cpa': return currency(row.meta_cpa);
      case 'impressions': return number(row.impressions);
      case 'clicks': return number(row.clicks);
      case 'ctr': return percent(row.ctr);
      case 'conversion_rate': return percent(row.conversion_rate);
      case 'days_until': return row.days_until != null ? number(row.days_until) : '\u2014';
      case 'expected_finish': return number(row.expected_finish);
      default: return '\u2014';
    }
  },

  buildCsvValue: (colId, row) => {
    const val = row[colId];
    return val == null ? '' : val;
  },
};
